import React, { useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import FileUpload from '../../../components/ui/FileUpload';
import { registrationService, getErrorMessage, getErrorCode, getErrorDetails } from '../../../services/apiService';
import ClearInputButton from './ClearInputButton';
import { useSlowFlag, useLookupTicket, manualReasonText } from './slowLookup';

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const mask = (acct) => (acct ? `••••${String(acct).slice(-4)}` : '');

const PATHS = [
  { id: 'cheque', icon: 'FileScan', title: 'Upload cancelled cheque', blurb: 'We read the account number and IFSC off it — nothing to type.' },
  { id: 'typed', icon: 'Keyboard', title: 'Type the details', blurb: 'Enter the account number and IFSC; we verify them with the bank.' },
];

const Chip = ({ icon, children, tone = 'neutral' }) => {
  const tones = {
    neutral: 'border-border bg-muted/40 text-text-secondary',
    success: 'border-success/30 bg-success/5 text-success',
    warning: 'border-warning/40 bg-warning/10 text-warning',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  );
};

const Notice = ({ tone = 'error', children }) => (
  <div
    className={`mt-3 rounded-md border px-3 py-2 text-sm ${
      tone === 'error' ? 'border-destructive/30 bg-destructive/5 text-destructive' : 'border-warning/40 bg-warning/10 text-foreground'
    }`}
    role={tone === 'error' ? 'alert' : 'status'}
  >
    {children}
  </div>
);

/**
 * Bank account verification for Indian vendors — two ways in, one verified card out.
 * Upload a cancelled cheque (we OCR the account number + IFSC and pre-fill them) or type the
 * details; either way the server runs the bank chain (account check → PAN linkage → name match).
 * The account check is pennyless when Surepass has it on, otherwise the ₹1 penny drop
 * and locks the result. (The old ₹1 UPI reverse-penny-drop path has been removed.)
 *
 * When the bank network cannot help — down, timed out, unreachable, or still working after 1.5 s —
 * the vendor may continue with the details typed in: the server records why (so a refresh keeps it
 * and the reviewer sees it), nothing is locked, and the account is verified before approval.
 */
const BankVerifyPanel = ({ sessionId, identity, kyc, formData, onResult, onPreviewDocument }) => {
  const features = kyc?.features || {};
  const bank = identity?.bank;
  const verified = Boolean(bank?.verified_at);
  // The bank network could not help and the vendor went on with the details typed in; a later verification clears this.
  const manual = !verified && Boolean(identity?.bank_manual);

  const ocrOn = features.cheque_ocr !== 'unavailable';
  const pennylessOn = features.bank_pennyless !== 'unavailable';

  const [path, setPath] = useState(ocrOn ? 'cheque' : 'typed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [warnings, setWarnings] = useState([]);
  // The server said the bank network did not answer, so the vendor may continue by hand.
  const [manualAllowed, setManualAllowed] = useState(false);
  const [continuing, setContinuing] = useState(false);
  // "Try verifying again" from the kept-as-typed card shows the panel once more.
  const [retrying, setRetrying] = useState(false);
  const lookup = useLookupTicket();
  const slow = useSlowFlag(busy);

  // typed path
  const [account, setAccount] = useState(formData.accountNumber || '');
  const [confirm, setConfirm] = useState('');
  const [ifsc, setIfsc] = useState(formData.ifscCode || '');

  // cheque path
  const [cheque, setCheque] = useState(null);
  const [ocr, setOcr] = useState(null); // { account_number, ifsc, confidence }
  // The manager's rule: the ₹1 check is not run until the vendor says the cheque and the fields agree —
  // OCR can drop a digit, and a wrong penny-drop still costs money.
  const [ocrAck, setOcrAck] = useState(false);
  useEffect(() => setOcrAck(false), [ocr]);

  const fail = (e) => {
    const code = getErrorCode(e);
    // Surepass said bank verification is not switched on for this account. Drop the gate so the vendor
    // can type the details and carry on — the reviewer verifies the account instead.
    if (code === "BANK_VERIFY_UNAVAILABLE") {
      setError(null);
      setErrorCode(null);
      onResult?.({ kyc: { ...(kyc || {}), features: { ...features, bank_pennyless: "unavailable" } } });
      return;
    }
    setError(getErrorMessage(e));
    setErrorCode(code);
    setManualAllowed(getErrorDetails(e)?.manual_allowed?.[0] === 'true');
  };
  const succeed = (data) => {
    setWarnings(data?.warnings || []);
    setError(null);
    setErrorCode(null);
    setManualAllowed(false);
    setRetrying(false);
    onResult?.(data);
  };

  // ---------- actions ----------
  const verifyTyped = async () => {
    if (!account || account !== confirm || !IFSC_RE.test(ifsc)) return;
    const ticket = lookup.start();
    setBusy(true);
    setManualAllowed(false);
    try {
      const res = await registrationService.verifyBank(sessionId, { account_number: account, ifsc, method: 'typed' });
      if (lookup.isCurrent(ticket)) succeed(res.data);
    } catch (e) {
      if (lookup.isCurrent(ticket)) fail(e);
    } finally {
      if (lookup.isCurrent(ticket)) setBusy(false);
    }
  };

  /** Go on with the details typed in — after the bank network failed, or while it is slow. */
  const continueManually = async () => {
    if (continuing) return;
    setContinuing(true);
    const walkingAway = busy;
    try {
      const typed = account && IFSC_RE.test(ifsc) ? { account_number: account, ifsc } : {};
      const res = await registrationService.bankManual(sessionId, typed);
      if (res?.success && res.data) {
        if (walkingAway) lookup.abandon();
        setBusy(false);
        setError(null);
        setErrorCode(null);
        setManualAllowed(false);
        setRetrying(false);
        onResult?.(res.data);
      } else {
        throw new Error(res?.message || 'Could not continue');
      }
    } catch (e) {
      setError(getErrorMessage(e));
      setErrorCode(getErrorCode(e));
    } finally {
      setContinuing(false);
    }
  };

  const readCheque = async (file) => {
    setCheque(file);
    setOcr(null);
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const res = await registrationService.chequeOcr(sessionId, file);
      const d = res.data;
      if (d.staged_file) onResult?.({ staged_file: d.staged_file });
      if (d.auto_verified && d.verification) {
        succeed(d.verification);
      } else {
        setOcr(d);
        setAccount(d.account_number || '');
        setConfirm(d.account_number || '');
        setIfsc(d.ifsc || '');
      }
    } catch (e) {
      fail(e);
      if (getErrorCode(e) === 'CHEQUE_OCR_UNAVAILABLE') setPath('typed');
    } finally {
      setBusy(false);
    }
  };

  const confirmOcr = async () => {
    const ticket = lookup.start();
    setBusy(true);
    setManualAllowed(false);
    try {
      const res = await registrationService.verifyBank(sessionId, { account_number: account, ifsc, method: 'cheque_ocr' });
      if (lookup.isCurrent(ticket)) succeed(res.data);
    } catch (e) {
      if (lookup.isCurrent(ticket)) fail(e);
    } finally {
      if (lookup.isCurrent(ticket)) setBusy(false);
    }
  };

  const changeAccount = async () => {
    setBusy(true);
    try {
      const res = await registrationService.clearBank(sessionId);
      setWarnings([]);
      setOcr(null);
      setCheque(null);
      onResult?.(res.data);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // ---------- verified card ----------
  if (verified) {
    const d = bank.ifsc_details || {};
    return (
      <div className="rounded-xl border border-success/30 bg-success/5 p-4 animate-rise-in motion-reduce:animate-none" role="status" aria-live="polite">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-success text-success-foreground animate-otp-pop motion-reduce:animate-none">
            <Icon name="ShieldCheck" size={20} strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-success">Bank account verified</p>
              <button
                type="button"
                onClick={changeAccount}
                disabled={busy}
                aria-label="Change bank account"
                title="Change bank account"
                data-testid="bank-change"
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-border bg-white px-2.5 py-1 text-xs font-medium text-text-secondary hover:border-destructive/40 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
              >
                <Icon name="X" size={12} /> Change account
              </button>
            </div>
            <p className="mt-0.5 text-lg font-semibold leading-tight text-foreground">{bank.holder_name || 'Account holder confirmed'}</p>
            <p className="text-sm text-text-secondary">
              {d.bank_name || d.bank || formData.bankName}
              {d.branch ? ` · ${d.branch}` : ''}
              {formData.accountNumber ? ` · ${mask(formData.accountNumber)}` : ''}
              {formData.ifscCode ? ` · ${formData.ifscCode}` : ''}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Chip icon="BadgeCheck" tone="success">Account exists</Chip>
              {bank.linked === true && <Chip icon="Link" tone="success">PAN linked</Chip>}
              {bank.linked === false && <Chip icon="Unlink" tone="warning">Not linked to your PAN</Chip>}
              {bank.linked === null && <Chip icon="HelpCircle">PAN link not checked</Chip>}
              {bank.account_type && <Chip icon="Wallet">{bank.account_type === 'current' ? 'Current account' : 'Savings account'}</Chip>}
              {typeof bank.name_match_score === 'number' && bank.name_match_score < 50 && (
                <Chip icon="AlertTriangle" tone="warning">Holder name differs from GST name</Chip>
              )}
              <Chip icon={bank.method === 'cheque_ocr' ? 'FileScan' : 'Keyboard'}>
                {bank.method === 'cheque_ocr' ? 'via cancelled cheque' : 'typed & verified'}
              </Chip>
            </div>
            {warnings.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-text-secondary">
                {warnings.map((w) => (
                  <li key={w.code} className="flex items-start gap-1.5"><Icon name="Info" size={12} className="mt-0.5 text-warning" />{w.message}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- kept as typed (the bank network could not help) ----------
  if (manual && !retrying && !busy) {
    return (
      <div className="rounded-xl border border-warning/40 bg-warning/5 p-4 animate-rise-in motion-reduce:animate-none" role="status" aria-live="polite" data-testid="bank-manual-card">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning">
            <Icon name="ShieldAlert" size={20} strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-warning">Bank details entered manually — not verified yet</p>
            <p className="mt-1.5 text-sm text-text-secondary">
              You continued because {manualReasonText(identity.bank_manual?.reason)}. Type your details below exactly as they appear on your cancelled
              cheque — the reviewer verifies the account before approval.
            </p>
            {pennylessOn && kyc?.enabled !== false && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={() => {
                  setRetrying(true);
                  setError(null);
                  setAccount(formData.accountNumber || account);
                  setConfirm(formData.accountNumber || confirm);
                  setIfsc(formData.ifscCode || ifsc);
                  setPath('typed');
                }}
              >
                Try verifying again
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- nothing available ----------
  if (!pennylessOn || kyc?.enabled === false) {
    return (
      <div className="rounded-xl border border-warning/40 bg-warning/10 p-4" role="status">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="mt-0.5 flex-shrink-0 text-warning" />
          <div>
            <p className="text-sm font-semibold text-foreground">Automatic bank verification is not switched on yet</p>
            <p className="text-sm text-text-secondary">Enter your bank details below and continue — the reviewer verifies the account before any payment.</p>
          </div>
        </div>
      </div>
    );
  }

  const available = PATHS.filter((p) => (p.id === 'cheque' ? ocrOn : true));
  const upstreamDown = errorCode === 'BANK_UPSTREAM_DOWN';

  return (
    <div className="rounded-xl border border-primary/30 bg-white p-4 shadow-sm animate-rise-in motion-reduce:animate-none">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon name="Landmark" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Verify the account we should pay into</p>
          <p className="text-xs text-text-secondary">Upload a cancelled cheque or type the details — either way the bank confirms the account holder.</p>
        </div>
      </div>

      {/* path picker */}
      <div className="mt-4 grid gap-2 sm:grid-cols-2" role="tablist" aria-label="Verification method">
        {available.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={path === p.id}
            onClick={() => { setPath(p.id); setError(null); }}
            disabled={busy}
            className={`rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              path === p.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
            }`}
          >
            <span className={`flex items-center gap-2 text-sm font-semibold ${path === p.id ? 'text-primary' : 'text-foreground'}`}>
              <Icon name={p.icon} size={16} /> {p.title}
            </span>
            <span className="mt-1 block text-xs text-text-secondary">{p.blurb}</span>
          </button>
        ))}
      </div>

      {/* cheque */}
      {path === 'cheque' && (
        <div className="mt-4 space-y-3">
          <FileUpload
            label="Cancelled cheque"
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={4 * 1024 * 1024}
            value={cheque}
            onChange={readCheque}
            onRemove={() => { setCheque(null); setOcr(null); }}
            onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'BANK_PROOF')}
            description="PDF, JPG or PNG · max 4 MB · this also serves as your bank proof document"
          />
          {busy && (
            <p className="flex items-center gap-2 text-sm text-text-secondary"><Icon name="Loader2" size={16} className="animate-spin text-primary" /> Reading the cheque…</p>
          )}
          {ocr && (
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-sm font-medium text-foreground">We read these from the cheque — confirm they are right</p>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <label className="text-xs text-text-secondary">
                  Account number
                  <input value={account} onChange={(e) => { setAccount(e.target.value.replace(/\D/g, '')); setConfirm(e.target.value.replace(/\D/g, '')); }} className="mt-1 flex h-10 w-full rounded-md border border-border bg-white px-3 font-mono text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
                </label>
                <label className="text-xs text-text-secondary">
                  IFSC
                  <input value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} maxLength={11} className="mt-1 flex h-10 w-full rounded-md border border-border bg-white px-3 font-mono text-sm uppercase text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
                </label>
              </div>
              <label className="mt-3 flex items-start gap-2 text-xs font-normal text-foreground">
                <input type="checkbox" checked={ocrAck} onChange={(e) => setOcrAck(e.target.checked)} className="mt-0.5" />
                I confirm the account number and IFSC above match my cancelled cheque.
              </label>
              <Button
                variant="default"
                onClick={confirmOcr}
                loading={busy}
                disabled={busy || !ocrAck || !account || !IFSC_RE.test(ifsc)}
                iconName="ShieldCheck"
                iconPosition="right"
                className="mt-3"
                title={ocrAck ? undefined : 'Tick the confirmation first — the bank check costs money and a mistyped digit would waste it'}
              >
                Confirm & verify
              </Button>
              <p className="mt-2 text-xs text-text-secondary">To confirm the account, the bank may credit ₹1 to it. Nothing is taken from it.</p>
            </div>
          )}
        </div>
      )}

      {/* typed */}
      {path === 'typed' && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="text-sm font-medium text-foreground">
            Account number <span className="text-destructive">*</span>
            <span className="relative mt-2 block">
              <input id="bank-account-input" value={account} onChange={(e) => { setAccount(e.target.value.replace(/\D/g, '')); setError(null); }} inputMode="numeric" autoComplete="off" disabled={busy} className="flex h-10 w-full rounded-md border border-border bg-white px-3 pr-10 font-mono text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60" />
              <ClearInputButton visible={Boolean(account) && !busy} label="Clear account number" testId="bank-clear-account" onClear={() => { setAccount(''); setConfirm(''); setError(null); }} />
            </span>
          </label>
          <label className="text-sm font-medium text-foreground">
            Confirm account number <span className="text-destructive">*</span>
            <span className="relative mt-2 block">
              <input id="bank-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))} inputMode="numeric" autoComplete="off" disabled={busy} onPaste={(e) => e.preventDefault()} className={`flex h-10 w-full rounded-md border bg-white px-3 pr-10 font-mono text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60 ${confirm && confirm !== account ? 'border-destructive' : 'border-border'}`} />
              <ClearInputButton visible={Boolean(confirm) && !busy} label="Clear confirmation" testId="bank-clear-confirm" onClear={() => setConfirm('')} />
            </span>
            {confirm && confirm !== account && <span className="mt-1 block text-xs font-normal text-destructive">Account numbers do not match</span>}
          </label>
          <label className="text-sm font-medium text-foreground">
            IFSC <span className="text-destructive">*</span>
            <span className="relative mt-2 block">
              <input id="bank-ifsc-input" value={ifsc} onChange={(e) => { setIfsc(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')); setError(null); }} maxLength={11} autoComplete="off" disabled={busy} className="flex h-10 w-full rounded-md border border-border bg-white px-3 pr-10 font-mono text-sm uppercase tracking-wider text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60" />
              <ClearInputButton visible={Boolean(ifsc) && !busy} label="Clear IFSC" testId="bank-clear-ifsc" onClear={() => { setIfsc(''); setError(null); }} />
            </span>
            {ifsc.length === 11 && !IFSC_RE.test(ifsc) && <span className="mt-1 block text-xs font-normal text-destructive">IFSC looks wrong (e.g. HDFC0000001)</span>}
          </label>
          <div className="flex items-end">
            <Button variant="default" onClick={verifyTyped} loading={busy} disabled={busy || !account || account !== confirm || !IFSC_RE.test(ifsc)} iconName="ShieldCheck" iconPosition="right" className="h-10 w-full sm:w-auto">
              Verify with the bank
            </Button>
          </div>
          <p className="text-xs text-text-secondary sm:col-span-2">To confirm the account, the bank may credit ₹1 to it. Nothing is taken from it.</p>
        </div>
      )}

      {/* The bank is slow: the vendor may stop waiting and go on with the details typed in. */}
      {busy && slow && (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning/5 px-3 py-2.5" role="status" data-testid="bank-slow-offer">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Icon name="Hourglass" size={16} className="text-warning" /> The bank is taking longer than usual to answer.
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            Keep waiting, or continue with these details — the reviewer verifies the account before approval.
          </p>
          <Button variant="outline" size="sm" className="mt-2" iconName="PencilLine" iconPosition="left" loading={continuing} disabled={continuing} onClick={continueManually}>
            Enter details manually
          </Button>
        </div>
      )}

      {error && (
        <Notice>
          <p className="flex items-center gap-2 font-medium"><Icon name="AlertCircle" size={16} /> {error}</p>
          {(manualAllowed || upstreamDown) && (
            <div className="mt-2 rounded-md border border-warning/40 bg-white px-3 py-2 text-foreground" data-testid="bank-manual-offer">
              <p className="text-xs text-text-secondary">
                You can continue and type your bank details yourself — nothing is locked, and the reviewer verifies the account before approval.
              </p>
              <Button variant="outline" size="sm" className="mt-2" iconName="PencilLine" iconPosition="left" loading={continuing} disabled={continuing} onClick={continueManually}>
                Enter details manually
              </Button>
            </div>
          )}
        </Notice>
      )}
    </div>
  );
};

export default BankVerifyPanel;
